import { generateUniqueId } from "@/utils/strings"

function install(app) {
  let tracker = null
  let tHandle = null
  let firstTimeout = null
  let lastSentSeconds = 0
  let sessionId = null
  let pendingEventSend = null

  const endpoint = '/calendar/userActivitySession'
  const intervalSec = 60

  const getSessionId = () => sessionId

  // Internal active time tracker (replaces useActiveTime)
  const createActiveTimeTracker = ({ inactivityMs = 20000 } = {}) => {
    let active = document.visibilityState === 'visible' && document.hasFocus()
    let last = performance.now()
    let acc = 0
    let inactivityTimer = null

    const tick = () => {
      if (!active) return
      const now = performance.now()
      acc += now - last
      last = now
    }

    const pause = () => {
      if (!active) return
      tick()
      active = false
    }

    const resume = () => {
      if (active) return
      active = true
      last = performance.now()
    }

    const markActivity = () => {
      clearTimeout(inactivityTimer)
      inactivityTimer = setTimeout(pause, inactivityMs)
      if (!active && document.visibilityState === 'visible' && document.hasFocus()) resume()
    }

    const onVis = () => document.visibilityState === 'visible' ? resume() : pause()
    const onFocus = () => resume()
    const onBlur = () => pause()

    const getActiveMs = () => { tick(); return Math.round(acc) }

    const start = () => {
      document.addEventListener('visibilitychange', onVis)
      window.addEventListener('focus', onFocus)
      window.addEventListener('blur', onBlur)
      ;['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart'].forEach(ev =>
        window.addEventListener(ev, markActivity, { passive: true }),
      )
      markActivity()
    }

    const stop = () => {
      pause()
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('blur', onBlur)
      ;['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart'].forEach(ev =>
        window.removeEventListener(ev, markActivity),
      )
      clearTimeout(inactivityTimer)
    }

    return { start, stop, getActiveMs }
  }

  const post = async ({ uniqueId, timeVisible }) => {
    if (!$backend) {
      console.warn('[user-activity-session] $backend is not available')

      return
    }
    try {
      await $backend(endpoint, { method: 'POST', body: { uniqueId, timeVisible } })
    } catch (e) {
      console.warn('[user-activity-session] post failed', e)
    }
  }

  const onPageHide = () => {
    try {
      const id = getSessionId()
      const ms = tracker ? tracker.getActiveMs() : lastSentSeconds * 1000
      const sec = Math.floor(ms / 1000)
      const effective = Math.max(sec, 0)
      const payload = new Blob([JSON.stringify({ uniqueId: id, timeVisible: effective })], { type: 'application/json' })
      console.log('onPageHide', endpoint)
      navigator.sendBeacon(import.meta.env.VITE_BACKEND_SERVER_URL + endpoint, payload)
      lastSentSeconds = effective
    } catch {}
  }

  const sendNow = () => {
    try {
      const ms = tracker ? tracker.getActiveMs() : lastSentSeconds * 1000
      const sec = Math.floor(ms / 1000)
      const effective = Math.max(sec, 0)
      lastSentSeconds = effective
      post({ uniqueId: getSessionId(), timeVisible: effective })
    } catch {}
  }

  const scheduleEventSend = () => {
    if (pendingEventSend) return
    pendingEventSend = setTimeout(() => {
      pendingEventSend = null
      sendNow()
    }, 1000)
  }

  const start = () => {
    sessionId = generateUniqueId()
    tracker = createActiveTimeTracker({ inactivityMs: 20000 })
    tracker.start()

    firstTimeout = setTimeout(() => {
      lastSentSeconds = 0
      post({ uniqueId: getSessionId(), timeVisible: lastSentSeconds })
    }, 10_000)

    tHandle = setInterval(() => {
      const ms = tracker.getActiveMs()
      const sec = Math.floor(ms / 1000)
      const effective = Math.max(sec, 0)
      lastSentSeconds = effective
      post({ uniqueId: getSessionId(), timeVisible: lastSentSeconds })
    }, intervalSec * 1000)

    //window.addEventListener('pagehide', onPageHide)
    document.addEventListener('visibilitychange', scheduleEventSend)
    //window.addEventListener('focus', scheduleEventSend)
    //window.addEventListener('blur', scheduleEventSend)
  }

  const stop = () => {
    clearTimeout(firstTimeout)
    clearInterval(tHandle)
    firstTimeout = null
    tHandle = null
    //window.removeEventListener('pagehide', onPageHide)
    document.removeEventListener('visibilitychange', scheduleEventSend)
    //window.removeEventListener('focus', scheduleEventSend)
    //window.removeEventListener('blur', scheduleEventSend)
    if (pendingEventSend) { clearTimeout(pendingEventSend); pendingEventSend = null }
    if (tracker) tracker.stop()
    tracker = null
  }

  // global registration
  if (app?.config?.globalProperties) {
    app.config.globalProperties.$startUserActivitySession = start
    app.config.globalProperties.$stopUserActivitySession = stop
  }

  // fallback przez window, gdyby loader nie podał app
  window.$startUserActivitySession = start
  window.$stopUserActivitySession = stop

  console.log('[user-activity-session] installed. has app =', !!app)
}

// handle both ways: app.use(plugin) and plugin(app)
export default function(app) {
  install(app)
}
export const installUserActivitySession = install
